import React, { FC } from 'react'
import { NavLink } from 'react-router-dom'
import cx from 'classnames'
import { MenuItem } from './MenuItem'
import { Icon } from 'components/Icon'
import { Tooltip } from 'components/Tooltip'
import { useTranslation } from 'react-i18next'
import { PopupMenu } from 'components/PopupMenu'
import { UserRoles } from 'models/user'
import { Text } from 'components/Text'
import { useLogout } from 'services/auth'
import { useFetchOrganization } from 'services/organization'
import { Flex } from 'components/Flex'
import { useUserContext } from 'context/user-context'
import styles from './Menu.module.scss'

type MenuProps = {
  hasShadow?: boolean
}

export const Menu: FC<MenuProps> = ({ children, ...props }) => {
  const { t } = useTranslation()
  const [currentUser] = useUserContext()
  const { organization } = useFetchOrganization()
  const [logout] = useLogout()
  const logo = organization && organization.logo

  const classes = cx({
    [styles.hasShadow]: props.hasShadow,
    [styles.menu]: styles.menu,
  })

  if (!currentUser) {
    return (
      <nav className={classes}>
        <Tooltip label={t('menu.login')}>
          <MenuItem path="/login">
            <Icon name="login-circle-line" size="xl" label={t('menu.login')} />
          </MenuItem>
        </Tooltip>
      </nav>
    )
  }

  const userIsAdmin = [UserRoles.MANAGER, UserRoles.OWNER].includes(currentUser.role)

  return (
    <nav className={classes}>
      <Tooltip label={t('menu.dashboard')}>
        <MenuItem path="/dashboard">
          <Icon name="slideshow-line" size="xl" label={t('menu.dashboard')} />
        </MenuItem>
      </Tooltip>

      <Tooltip label={t('menu.explore')}>
        <MenuItem path="/explore">
          <Icon name="compass-discover-line" size="xl" label={t('menu.explore')} />
        </MenuItem>
      </Tooltip>

      <Tooltip label={t('menu.my_projects')}>
        <MenuItem path="/projects">
          <Icon name="folder-4-line" size="xl" label={t('menu.my_projects')} />
        </MenuItem>
      </Tooltip>

      <PopupMenu
        label={t('menu.organization')}
        className={styles.userMenuList}
        triggerOffset={10}
        trigger={
          <Tooltip label={t('menu.organization')}>
            <MenuItem>
              {logo ? <img src={logo.url} alt="" /> : <Icon name="building-line" size="xl" />}
            </MenuItem>
          </Tooltip>
        }
      >
        {organization?.subscription.level !== "Freemium" &&
          <NavLink
            className={styles.userMenuItem}
            activeClassName={styles.userMenuItemActive}
            to="/account"
          >
            <Flex justify="between" align="center">
              <Text weight="strong">{t('menu.organization_menu.account')}</Text>
              <Icon name="user-line" size="xl" />
            </Flex>
          </NavLink>
        }

        {userIsAdmin && organization?.subscription.level !== "Freemium" && (
          <NavLink
            className={styles.userMenuItem}
            activeClassName={styles.userMenuItemActive}
            to="/datasets"
          >
            <Flex justify="between" align="center">
              <Text weight="strong">{t('menu.organization_menu.datasets')}</Text>
              <Icon name="braces-line" size="xl" />
            </Flex>
          </NavLink>
        )}

        {userIsAdmin && organization?.subscription.level !== "Freemium" && (
          <NavLink
            className={styles.userMenuItem}
            activeClassName={styles.active}
            to="/organization/users"
          >
            <Flex justify="between" align="center">
              <Text weight="strong">{t('menu.organization_menu.coworkers')}</Text>
              <Icon name="group-line" size="xl" />
            </Flex>
          </NavLink>
        )}

        {/*UserRoles.OWNER === currentUser!.role && (
          <NavLink
            className={styles.userMenuItem}
            activeClassName={styles.userMenuItemActive}
            to="/billing"
          >
            <Flex justify="between" align="center">
              <Text weight="strong">{t('menu.organization_menu.billing')}</Text>
              <Icon name="briefcase-3-line" size="xl" />
            </Flex>
          </NavLink>
        )*/}

        {userIsAdmin && organization?.subscription.level !== "Freemium" && (
          <NavLink
            className={styles.userMenuItem}
            activeClassName={styles.active}
            to="/organization/settings"
          >
            <Flex justify="between" align="center">
              <Text weight="strong">{t('menu.organization_menu.organization')}</Text>
              <Icon name="building-line" size="xl" />
            </Flex>
          </NavLink>
        )}

        <button className={styles.userMenuItem} onClick={() => logout()}>
          <Flex justify="between" align="center">
            <Text weight="strong">{t('menu.organization_menu.logout')}</Text>
            <Icon name="login-circle-line" size="xl" />
          </Flex>
        </button>
      </PopupMenu>
    </nav>
  )
}

Menu.displayName = 'Menu'
